import { useCallback, useEffect, useMemo, useState } from "react";
import type { Refs, Row } from "../types";
import { api, ApiError, type ItemAlmox, type OrdemServico } from "../api";
import { ordenarPorNome } from "../ordem";
import {
  IconClose,
  IconUnlock,
  IconLock,
  IconSearch,
  IconTruck,
  IconTrash,
  IconPlus,
} from "../icons";

interface Props {
  refs: Refs;
  liberacao?: Row | null; // edição de uma liberação existente
  equipamentoId?: number | string; // pré-seleção do equipamento (ao criar da agenda)
  onClose: () => void;
  onSaved: () => void;
}

const STATUS = ["Liberado", "Pendente", "Bloqueado", "Devolvido"];

/** Data de hoje em AAAA-MM-DD no fuso LOCAL (não UTC — evita gravar o dia seguinte à noite). */
function hojeISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}
const dateVal = (v: unknown) => (v ? String(v).slice(0, 10) : "");

/** Compara nomes ignorando acento, caixa e espaço repetido (casar o responsável da OS). */
function normNome(s: string): string {
  return (s ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Um insumo escolhido para o Almoxarifado separar. */
interface ItemPedido {
  item: ItemAlmox;
  quantidade: string;
}

/**
 * Cria/edita a LIBERAÇÃO de um equipamento de ensaio.
 *
 * O questionário começa pelo NÚMERO DA OS: ao informá-lo, buscamos a ordem de
 * serviço no sistema de Relatórios (dono do dado) e preenchemos sozinhos o
 * cliente, o tipo de serviço, o período e o responsável — do mesmo jeito que o
 * Relatórios faz. Também dá para listar aqui os insumos que o Almoxarifado
 * precisa separar para essa OS: ao salvar, a solicitação de separação é criada
 * lá com o número da OS no pedido.
 */
export default function LiberacaoModal({ refs, liberacao, equipamentoId, onClose, onSaved }: Props) {
  const [equipamento_id, setEquipamento] = useState<string>(
    String(liberacao?.equipamento_id ?? equipamentoId ?? ""),
  );
  const [obra_id, setObra] = useState<string>(String(liberacao?.obra_id ?? ""));
  const [colaborador_id, setColaborador] = useState<string>(String(liberacao?.colaborador_id ?? ""));
  const [atividade, setAtividade] = useState<string>(liberacao?.atividade ?? "");
  const [os_numero, setOsNumero] = useState<string>(String(liberacao?.os_numero ?? ""));
  const [data_liberacao, setDataLiberacao] = useState<string>(
    liberacao ? dateVal(liberacao.data_liberacao) : hojeISO(),
  );
  const [data_retorno, setDataRetorno] = useState<string>(dateVal(liberacao?.data_retorno));
  const [status, setStatus] = useState<string>(liberacao?.status ?? "Liberado");
  const [observacao, setObservacao] = useState<string>(liberacao?.observacao ?? "");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── OS vinda do Relatórios (preenchimento automático) ──
  const [os, setOs] = useState<OrdemServico | null>(null);
  const [osBuscando, setOsBuscando] = useState(false);
  const [osErro, setOsErro] = useState<string | null>(null);

  // ── Insumos para o Almoxarifado separar ──
  const [pedirAlmox, setPedirAlmox] = useState(false);
  const [itensAlmox, setItensAlmox] = useState<ItemAlmox[]>([]);
  const [almoxErro, setAlmoxErro] = useState<string | null>(null);
  const [buscaInsumo, setBuscaInsumo] = useState("");
  const [pedido, setPedido] = useState<ItemPedido[]>([]);
  const [prioridade, setPrioridade] = useState("Normal");

  // OS (obra) local escolhida — segue valendo para quem não usa número de OS.
  const obra = useMemo(
    () => refs.obras.find((o) => String(o.id) === obra_id) ?? null,
    [refs.obras, obra_id],
  );

  // O que aparece nos campos de leitura: a OS do Relatórios manda; sem ela,
  // vale a obra cadastrada aqui.
  const cliente = os?.cliente ?? (obra?.cliente as string | undefined) ?? "";
  const tipoServico = os?.tipoServico ?? (obra?.tipo_servico as string | undefined) ?? "";

  const equipOptions = useMemo(() => {
    // Equipamentos em ordem alfabética (src/ordem.ts). O equipamento gravado
    // que já saiu do cadastro (só sobrou o id) continua indo para o topo.
    const list = ordenarPorNome(
      refs.equipamentos.map((eq) => ({
        id: String(eq.id),
        nome: String(eq.nome ?? `Equipamento #${eq.id}`),
      })),
      (eq) => eq.nome,
    );
    if (equipamento_id && !list.some((e) => e.id === equipamento_id)) {
      list.unshift({ id: equipamento_id, nome: `Equipamento #${equipamento_id}` });
    }
    return list;
  }, [refs.equipamentos, equipamento_id]);

  // Fecha com Escape (acessibilidade de teclado).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  /**
   * Busca a OS e preenche o que ainda estiver em branco. Campos já digitados
   * NÃO são sobrescritos — quem preencheu à mão tinha um motivo.
   */
  const buscarOs = useCallback(
    async (numero: string) => {
      const n = numero.trim();
      if (!n) {
        setOs(null);
        setOsErro(null);
        return;
      }
      setOsBuscando(true);
      setOsErro(null);
      try {
        const encontrada = await api.relatorios.buscarOs(n);
        setOs(encontrada);
        setAtividade((v) => v || encontrada.tipoServico || "");
        setDataLiberacao((v) => v || encontrada.inicio || "");
        setDataRetorno((v) => v || encontrada.fim || "");
        // Responsável da OS → "Liberado para", casando pelo nome do cadastro.
        setColaborador((v) => {
          if (v) return v;
          const alvo = normNome(encontrada.responsavel ?? "");
          const achado = refs.colaboradores.find((c) => normNome(String(c.nome ?? "")) === alvo);
          return achado ? String(achado.id) : v;
        });
      } catch (err) {
        setOs(null);
        setOsErro(err instanceof ApiError ? err.message : "Falha ao consultar a OS");
      } finally {
        setOsBuscando(false);
      }
    },
    [refs.colaboradores],
  );

  // Ao abrir uma liberação já gravada, recarrega os dados da OS para exibição.
  useEffect(() => {
    if (liberacao?.os_numero) buscarOs(String(liberacao.os_numero));
    // Só na abertura: depois a busca é disparada pelo próprio campo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Carrega o estoque do Almoxarifado só quando a pessoa abre essa parte. */
  const carregarInsumos = useCallback(async () => {
    setAlmoxErro(null);
    try {
      setItensAlmox(await api.almox.listItens());
    } catch (err) {
      setAlmoxErro(err instanceof ApiError ? err.message : "Falha ao consultar o Almoxarifado");
    }
  }, []);

  useEffect(() => {
    if (pedirAlmox && itensAlmox.length === 0 && !almoxErro) carregarInsumos();
  }, [pedirAlmox, itensAlmox.length, almoxErro, carregarInsumos]);

  const sugestoes = useMemo(() => {
    const q = buscaInsumo.trim().toLowerCase();
    if (!q) return [];
    const jaNoPedido = new Set(pedido.map((p) => p.item.id));
    return itensAlmox
      .filter((i) => !jaNoPedido.has(i.id))
      .filter((i) =>
        [i.descricao, i.codigo, i.categoria].some((v) => String(v ?? "").toLowerCase().includes(q)),
      )
      .slice(0, 8);
  }, [buscaInsumo, itensAlmox, pedido]);

  function adicionarItem(item: ItemAlmox) {
    setPedido((p) => [...p, { item, quantidade: "1" }]);
    setBuscaInsumo("");
  }
  function removerItem(id: number) {
    setPedido((p) => p.filter((x) => x.item.id !== id));
  }
  function setQuantidade(id: number, valor: string) {
    setPedido((p) => p.map((x) => (x.item.id === id ? { ...x, quantidade: valor } : x)));
  }

  async function salvar() {
    if (!equipamento_id) {
      setError("Selecione o equipamento de ensaio.");
      return;
    }
    // A OS pode vir pelo número (Relatórios) ou pela obra cadastrada aqui.
    if (!os_numero.trim() && !obra_id) {
      setError("Informe o número da OS ou selecione a obra.");
      return;
    }
    if (data_liberacao && data_retorno && data_retorno < data_liberacao) {
      setError("A data de retorno não pode ser anterior à data de liberação.");
      return;
    }
    if (pedirAlmox && pedido.length > 0) {
      const invalido = pedido.find((p) => !(Number(p.quantidade) > 0));
      if (invalido) {
        setError(`Informe uma quantidade válida para "${invalido.item.descricao}".`);
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      const payload: Row = {
        equipamento_id: Number(equipamento_id),
        obra_id: obra_id ? Number(obra_id) : null,
        colaborador_id: colaborador_id ? Number(colaborador_id) : null,
        atividade: atividade.trim() || null,
        os_numero: os_numero.trim() || null,
        data_liberacao: data_liberacao || null,
        data_retorno: data_retorno || null,
        status,
        observacao: observacao.trim() || null,
      };
      if (liberacao?.id) {
        await api.update("liberacoes", liberacao.id, payload);
      } else {
        await api.create("liberacoes", payload);
      }

      // A liberação já está gravada. A solicitação ao Almoxarifado vem depois e
      // NÃO desfaz a liberação se falhar — avisamos e a pessoa refaz o pedido
      // pela aba Almoxarifado, sem perder o que já foi salvo.
      if (pedirAlmox && pedido.length > 0) {
        try {
          const r = await api.almox.criarSolicitacoesLote({
            itens: pedido.map((p) => ({ item_id: p.item.id, quantidade: Number(p.quantidade) })),
            prioridade,
            os_numero: os_numero.trim(),
            cliente,
            atividade: atividade.trim(),
            observacao: observacao.trim(),
          });
          if (r.falhas?.length) {
            setError(
              `Liberação salva. ${r.criadas} item(ns) foram para o Almoxarifado, mas ${r.falhas.length} falharam — refaça esses pela aba Almoxarifado.`,
            );
            setSaving(false);
            return;
          }
        } catch (err) {
          setError(
            `Liberação salva, mas a solicitação ao Almoxarifado falhou: ${
              err instanceof ApiError ? err.message : "erro inesperado"
            }. Refaça o pedido pela aba Almoxarifado.`,
          );
          setSaving(false);
          return;
        }
      }

      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao salvar a liberação");
    } finally {
      setSaving(false);
    }
  }

  const liberado = status === "Liberado";

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-label={liberacao?.id ? "Liberação do equipamento" : "Nova liberação de equipamento"}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h3>
            {liberado ? <IconUnlock size={18} /> : <IconLock size={18} />}{" "}
            {liberacao?.id ? "Liberação do equipamento" : "Nova liberação de equipamento"}
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          {/* Liberar ou não — botões grandes (o status é a "liberação ou não"). */}
          <div className="lib-toggle">
            <button
              type="button"
              className={`lib-toggle-btn${liberado ? " on" : ""}`}
              onClick={() => setStatus("Liberado")}
            >
              <IconUnlock size={16} /> Liberado
            </button>
            <button
              type="button"
              className={`lib-toggle-btn danger${status === "Bloqueado" ? " on" : ""}`}
              onClick={() => setStatus("Bloqueado")}
            >
              <IconLock size={16} /> Não liberado
            </button>
          </div>

          <div className="form-grid">
            {/* O número da OS vem primeiro: é ele que preenche o resto. */}
            <label className="field">
              <span>Número da OS</span>
              <input
                type="text"
                value={os_numero}
                placeholder="Ex.: 1234"
                onChange={(e) => setOsNumero(e.target.value)}
                onBlur={(e) => buscarOs(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    buscarOs(os_numero);
                  }
                }}
              />
              <small className="hint">
                {osBuscando
                  ? "Procurando a OS…"
                  : os
                    ? "Preenchido automaticamente pela OS."
                    : "Informe a OS para preencher o restante sozinho."}
              </small>
            </label>

            <label className="field">
              <span>
                Equipamento de ensaio<em className="req"> *</em>
              </span>
              <select value={equipamento_id} autoFocus onChange={(e) => setEquipamento(e.target.value)}>
                <option value="">Selecione...</option>
                {equipOptions.map((eq) => (
                  <option key={eq.id} value={eq.id}>
                    {eq.nome}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Cliente</span>
              <input
                type="text"
                value={cliente}
                readOnly
                placeholder="— vem da OS —"
              />
            </label>

            <label className="field">
              <span>Tipo de serviço</span>
              <input
                type="text"
                value={tipoServico}
                readOnly
                placeholder="— vem da OS —"
              />
            </label>

            <label className="field">
              <span>Obra (cadastro local)</span>
              <select value={obra_id} onChange={(e) => setObra(e.target.value)}>
                <option value="">— opcional, quando não há OS —</option>
                {refs.obras.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nome}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Liberado para</span>
              <select value={colaborador_id} onChange={(e) => setColaborador(e.target.value)}>
                <option value="">Selecione...</option>
                {refs.colaboradores.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Atividade</span>
              <input type="text" value={atividade} onChange={(e) => setAtividade(e.target.value)} />
            </label>

            <label className="field">
              <span>Data de liberação</span>
              <input type="date" value={data_liberacao} onChange={(e) => setDataLiberacao(e.target.value)} />
            </label>

            <label className="field">
              <span>Data de retorno</span>
              <input type="date" value={data_retorno} onChange={(e) => setDataRetorno(e.target.value)} />
            </label>

            <label className="field">
              <span>Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>

            <label className="field field-wide">
              <span>Observação</span>
              <textarea rows={2} value={observacao} onChange={(e) => setObservacao(e.target.value)} />
            </label>
          </div>

          {/* Resumo do que veio da OS — mostra de onde saiu o preenchimento. */}
          {os && (
            <div className="os-resumo">
              <strong>
                OS {os.numero} · {os.cliente}
              </strong>
              <span>
                {[
                  os.equipamento,
                  os.tipoServico,
                  os.local,
                  os.inicio && os.fim ? `${os.inicio} → ${os.fim}` : "",
                  os.responsavel ? `Resp.: ${os.responsavel}` : "",
                  `Situação: ${os.status}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
          )}
          {osErro && <div className="hint hint-warn">{osErro} Você pode preencher à mão.</div>}

          {/* ── Solicitação ao Almoxarifado ── */}
          <div className="almox-pedido">
            <label className="almox-check">
              <input
                type="checkbox"
                checked={pedirAlmox}
                onChange={(e) => setPedirAlmox(e.target.checked)}
              />
              <IconTruck size={15} /> Pedir ao Almoxarifado que separe os itens desta OS
            </label>

            {pedirAlmox && (
              <>
                {almoxErro ? (
                  <div className="hint hint-warn">
                    {almoxErro}{" "}
                    <button type="button" className="link-btn" onClick={carregarInsumos}>
                      tentar de novo
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="form-grid">
                      <label className="field field-wide">
                        <span>Buscar insumo</span>
                        <div className="search-wrap">
                          <IconSearch size={16} />
                          <input
                            className="search"
                            value={buscaInsumo}
                            placeholder="Código, descrição ou categoria…"
                            onChange={(e) => setBuscaInsumo(e.target.value)}
                          />
                        </div>
                      </label>
                      <label className="field">
                        <span>Prioridade</span>
                        <select value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>
                          <option value="Normal">Normal</option>
                          <option value="Alta">Alta</option>
                          <option value="Urgente">Urgente</option>
                        </select>
                      </label>
                    </div>

                    {sugestoes.length > 0 && (
                      <ul className="almox-sugestoes">
                        {sugestoes.map((i) => (
                          <li key={i.id}>
                            <button type="button" onClick={() => adicionarItem(i)}>
                              <IconPlus size={14} />
                              <span>{i.descricao}</span>
                              <em>
                                {i.codigo || "—"} · saldo {i.saldo} {i.unidade}
                              </em>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {pedido.length === 0 ? (
                      <p className="hint">
                        Nenhum item na lista ainda — busque acima e clique para adicionar.
                      </p>
                    ) : (
                      <table className="table table-compact">
                        <thead>
                          <tr>
                            <th>Insumo</th>
                            <th>Disponível</th>
                            <th>Quantidade</th>
                            <th className="col-actions"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {pedido.map((p) => (
                            <tr key={p.item.id}>
                              <td>
                                {p.item.descricao}
                                <br />
                                <small>{p.item.codigo}</small>
                              </td>
                              <td>
                                {p.item.saldo} {p.item.unidade}
                              </td>
                              <td>
                                <input
                                  type="number"
                                  step="any"
                                  min="0"
                                  className="qtd-input"
                                  value={p.quantidade}
                                  onChange={(e) => setQuantidade(p.item.id, e.target.value)}
                                />
                                {Number(p.quantidade) > p.item.saldo && (
                                  <small className="hint hint-warn">acima do saldo</small>
                                )}
                              </td>
                              <td className="col-actions">
                                <button
                                  type="button"
                                  className="icon-btn"
                                  title="Remover da lista"
                                  onClick={() => removerItem(p.item.id)}
                                >
                                  <IconTrash size={15} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {pedido.length > 0 && (
                      <p className="hint">
                        Ao salvar, o Almoxarifado recebe {pedido.length} solicitação(ões) de
                        separação{os_numero.trim() ? ` com a OS ${os_numero.trim()}` : ""}.
                      </p>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {error && <div className="form-error">{error}</div>}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={salvar} disabled={saving}>
            {saving ? "Salvando…" : "Salvar liberação"}
          </button>
        </div>
      </div>
    </div>
  );
}
